import * as React from "react";
import { cn } from "@/lib/utils";
import { Button, type ButtonProps } from "./button";
import { IconClose, IconBox, IconCommand } from "./icons";

export interface ContextBarButton {
  id: string;
  text: string;
  icon?: React.ComponentType<{ className?: string }>;
  onClick: () => void;
  variant?: ButtonProps["variant"];
  className?: string;
}

interface ContextBarProps extends React.HTMLAttributes<HTMLDivElement> {
  count: number;
  onClose?: () => void;
  onCommand?: () => void;
  buttons?: ContextBarButton[];
}

const ContextBar = React.forwardRef<HTMLDivElement, ContextBarProps>(
  ({ className, count, onClose, onCommand, buttons = [], ...props }, ref) => {
    return (
      <div
        ref={ref}
        className={cn(
          "fixed bottom-10 left-1/2 -translate-x-1/2 z-50",
          "flex items-center gap-2",
          "bg-background-primary border border-border-primary rounded-lg p-2",
          className
        )}
        {...props}
      >
        <Button
          variant="tertiary"
          size="sm"
          style={{
            borderWidth: "1px",
            borderStyle: "solid",
            borderColor: "var(--secondary-n-950)",
          }}
          onClick={onClose}
          className="p-1 pl-2 h-auto flex items-center gap-1"
        >
          <span>{count} selected</span>
          <IconClose className="h-4 w-4" />
        </Button>

        {buttons.map((button) => {
          const Icon = button.icon;
          return (
            <Button
              key={button.id}
              variant={button.variant || "outline"}
              size="sm"
              style={{
                borderWidth: "1px",
                borderStyle: "solid",
                borderColor: "var(--secondary-n-900)",
              }}
              className={cn(
                "p-1 pr-2 h-auto flex items-center gap-1",
                button.className
              )}
              onClick={button.onClick}
            >
              {Icon && <Icon className="h-4 w-4 mr-1" />}
              {button.text}
            </Button>
          );
        })}

        <Button
          variant="outline"
          size="sm"
          style={{
            borderWidth: "1px",
            borderStyle: "solid",
            borderColor: "var(--secondary-n-900)",
          }}
          className="p-1 pr-2 h-auto flex items-center gap-1"
          onClick={onCommand}
        >
          <IconCommand className="h-4 w-4 mr-1" />
          Command
        </Button>
      </div>
    );
  }
);

ContextBar.displayName = "ContextBar";

export { ContextBar };
